"use client";

import { useState, useRef, useEffect } from "react";
import { useAuth } from "../providers/AuthProvider";

interface Props {
  onComplete: () => void;
}

export function DisplayNameStep({ onComplete }: Props) {
  const { updateUser } = useAuth();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-focus with a small delay to ensure the element is mounted
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);

    const result = await updateUser({ displayName: trimmed });
    setLoading(false);

    if (result.ok) {
      onComplete();
    } else {
      setError(result.error ?? "Failed to save name");
    }
  }

  return (
    <form onSubmit={handleSubmit} style={formStyle}>
      <div style={headerStyle}>What should we call you?</div>
      <input
        ref={inputRef}
        type="text"
        placeholder="Your name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={inputStyle}
        maxLength={100}
        autoComplete="name"
      />
      {error && <p style={errorStyle}>{error}</p>}
      <button type="submit" style={buttonStyle} disabled={loading || !name.trim()}>
        {loading ? "Saving..." : "Continue"}
      </button>
    </form>
  );
}

const formStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
  width: "100%",
  maxWidth: 320,
};

const headerStyle: React.CSSProperties = {
  fontSize: "1.1rem",
  fontWeight: 600,
  color: "var(--color-text)",
  textAlign: "center",
  marginBottom: "0.25rem",
};

const inputStyle: React.CSSProperties = {
  padding: "0.6rem 0.75rem",
  fontSize: "1rem",
  background: "var(--color-bg-input)",
  color: "var(--color-text)",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--color-border-light)",
  borderRadius: 4,
  outline: "none",
  textAlign: "center",
};

const buttonStyle: React.CSSProperties = {
  padding: "0.6rem 1rem",
  fontSize: "0.85rem",
  background: "var(--color-btn-primary-bg)",
  color: "var(--color-btn-primary-text)",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
  fontWeight: 600,
};

const errorStyle: React.CSSProperties = {
  fontSize: "0.8rem",
  color: "var(--color-text-tertiary)",
  margin: 0,
  textAlign: "center",
};
